import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppContext } from '../App';
import NetworkMap from '../components/NetworkMap';

const API = 'https://api.cpms-srlkevcharge.com';

// ── Icons ─────────────────────────────────────────────────────────────────
const IconBuilding = () => (
  <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <path d="M3 21h18" />
    <path d="M5 21V7l8-4v18" />
    <path d="M19 21V11l-6-4" />
    <path d="M9 9v.01" /><path d="M9 12v.01" /><path d="M9 15v.01" /><path d="M9 18v.01" />
  </svg>
);
const IconArrow = () => (
  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);

const IconTrashOrg = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);

const IconBuildingSmall = () => (
  <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <path d="M3 21h18" />
    <path d="M5 21V7l8-4v18" />
    <path d="M19 21V11l-6-4" />
    <path d="M9 9v.01" /><path d="M9 12v.01" /><path d="M9 15v.01" /><path d="M9 18v.01" />
  </svg>
);
const IconPin = () => (
  <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
  </svg>
);
const IconPlugSmall = () => (
  <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <path d="M18 7V5a2 2 0 00-2-2H8a2 2 0 00-2 2v2M12 22V13" /><path d="M6 7h12a2 2 0 012 2v3a5 5 0 01-5 5H9a5 5 0 01-5-5V9a2 2 0 012-2z" />
  </svg>
);
const IconGridSmall = () => (
  <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);
const IconBoltSmall = () => (
  <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);
const IconAlertSmall = () => (
  <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Math.floor((Date.now() - new Date(dateStr + 'Z')) / 1000);
  if (diff < 30 && diff > -30) return 'Just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(dateStr + 'Z').toLocaleDateString('en-GB');
}

function durationSince(startTime) {
  const diff = Math.floor((Date.now() - new Date(startTime + 'Z')) / 1000);
  const m = Math.floor(diff / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  return `${m}m`;
}

export default function Home() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { liveEvents, showToast } = useAppContext();
  const navigate = useNavigate();

  const fetchOverview = async () => {
    try {
      const res = await axios.get(`${API}/organisations/overview`);
      setData(res.data);
    } catch (e) {
      console.error('Failed to fetch overview', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrg = async (e, org) => {
    e.stopPropagation();
    if (org.location_count > 0) {
      showToast(`Cannot delete "${org.name}" — it still has ${org.location_count} station(s) registered. Remove those first.`, 'error');
      return;
    }
    if (!window.confirm(`Delete organisation "${org.name}"? This cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/organisations/${org.id}`);
      showToast(`Organisation "${org.name}" removed successfully`);
      fetchOverview();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to delete organisation', 'error');
    }
  };

  useEffect(() => { fetchOverview(); }, []);
  useEffect(() => { if (liveEvents.length > 0) fetchOverview(); }, [liveEvents]);

  if (loading) return <div className="loading">Loading network overview...</div>;
  if (!data) return <div className="loading">No data available</div>;

  const { metrics, organisations, live_activity, stations } = data;

  return (
    <>
      <div className="page-header">
        <h1>Network Overview</h1>
        <p>Real-time status across all organisations, locations, and chargers</p>
      </div>

      {/* ── Metric Strip ── */}
      <div className="metric-strip">
        <div className="metric-tile">
          <div className="metric-tile-icon-badge color-blue"><IconBuildingSmall /></div>
          <div className="metric-tile-label">Organisations</div>
          <div className="metric-tile-value">{metrics.organisations}</div>
        </div>
        <div className="metric-tile clickable" onClick={() => navigate('/evse')}>
          <div className="metric-tile-icon-badge color-purple"><IconPin /></div>
          <div className="metric-tile-label">Locations</div>
          <div className="metric-tile-value">{metrics.locations}</div>
        </div>
        <div className="metric-tile clickable" onClick={() => navigate('/evse')}>
          <div className="metric-tile-icon-badge color-green"><IconGridSmall /></div>
          <div className="metric-tile-label">Chargers</div>
          <div className="metric-tile-value">{metrics.chargers}</div>
        </div>
        <div className="metric-tile clickable" onClick={() => navigate('/evse')}>
          <div className="metric-tile-icon-badge color-teal"><IconPlugSmall /></div>
          <div className="metric-tile-label">Connectors</div>
          <div className="metric-tile-value">{metrics.connectors}</div>
        </div>
        <div className="metric-tile highlight pulse-glow clickable" onClick={() => navigate('/sessions')}>
          <div className="metric-tile-icon-badge color-amber"><IconBoltSmall /></div>
          <div className="metric-tile-label">Live Sessions</div>
          <div className="metric-tile-value">{metrics.live_sessions}</div>
        </div>
        <div className="metric-tile clickable" onClick={() => navigate('/evse?status=Offline')}>
          <div className="metric-tile-icon-badge color-red"><IconAlertSmall /></div>
          <div className="metric-tile-label">Offline / Faulted</div>
          <div className="metric-tile-value">{metrics.offline_faulted}</div>
        </div>
      </div>

      {/* ── Network Map ── */}
      <NetworkMap
        stations={stations || []}
        onStationClick={(station) => {
          const org = organisations.find(o => o.name === station.organisation_name);
          if (org) navigate(`/organisation/${org.id}`);
        }}
      />

      {/* ── Organisations Grid ── */}
      <div className="section-heading">
        <div>
          <h2>Organisations</h2>
          <p>Click into an organisation to view their stations</p>
        </div>
      </div>

      {organisations.length === 0 ? (
        <div className="card empty-state">
          <h3>No organisations registered yet</h3>
          <p>Add your first EVSE to get started. This will create your first organisation.</p>
          <button className="btn btn-green" onClick={() => navigate('/add-charger')}>
            + Add EVSE
          </button>
        </div>
      ) : (
        <div className="org-grid">
          {organisations.map(org => (
            <div key={org.id} className="org-card" onClick={() => navigate(`/organisation/${org.id}`)}>
              <div className="org-card-top">
                <div>
                  <div className="org-card-icon-badge"><IconBuilding /></div>
                  <div className="org-card-name">{org.name}</div>
                  <div className="org-card-sub">{org.location_count} location{org.location_count !== 1 ? 's' : ''}</div>
                </div>
                <button
                  className="org-card-delete-btn"
                  onClick={(e) => handleDeleteOrg(e, org)}
                  title="Delete organisation"
                >
                  <IconTrashOrg />
                </button>
              </div>

              <div className="org-card-stats">
                <div>
                  <div className="org-card-stat-value">{org.charger_count}</div>
                  <div className="org-card-stat-label">Chargers</div>
                </div>
                <div>
                  <div className="org-card-stat-value">{org.connector_count}</div>
                  <div className="org-card-stat-label">Connectors</div>
                </div>
                <div>
                  <div className="org-card-stat-value">{org.charging}</div>
                  <div className="org-card-stat-label">Charging Now</div>
                </div>
              </div>

              <div className="org-health-bar">
                {org.available_pct > 0 && <div className="org-health-segment available" style={{ width: `${org.available_pct}%` }} />}
                {org.charging_pct > 0 && <div className="org-health-segment charging" style={{ width: `${org.charging_pct}%` }} />}
                {org.offline_pct > 0 && <div className="org-health-segment offline" style={{ width: `${org.offline_pct}%` }} />}
              </div>
              <div className="org-health-legend">
                <span><span className="org-health-dot available" />{org.available} Available</span>
                <span><span className="org-health-dot charging" />{org.charging} Charging</span>
                <span><span className="org-health-dot offline" />{org.offline} Offline</span>
              </div>

              <div className="org-card-arrow">
                View stations <IconArrow />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Live Network Activity ── */}
      <div className="section-heading">
        <div>
          <h2>Live Network Activity</h2>
          <p>Chargers currently in an active session, across all organisations</p>
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          {live_activity.length === 0 ? (
            <div className="empty-state">
              <h3>No active sessions right now</h3>
              <p>Live charging sessions across the network will appear here.</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Organisation</th>
                  <th>Station</th>
                  <th>Charger</th>
                  <th>Connector</th>
                  <th>Started</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {live_activity.map(session => (
                  <tr key={session.session_id}>
                    <td><span className="live-pulse-dot" />Charging</td>
                    <td style={{ fontWeight: 600 }}>{session.organisation_name}</td>
                    <td>{session.station_name}</td>
                    <td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12.5 }}>{session.ocpp_id}</td>
                    <td>{session.connector_type || '—'}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{timeAgo(session.start_time)}</td>
                    <td style={{ fontWeight: 600 }}>{durationSince(session.start_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}